import StoreApi from '@api/store'
import {
  ORDER_TYPE_NEW,
  ORDER_TYPE_APPEND,
  ORDER_TYPE_RENEW,
  ORDER_TYPE_UPGRADE,
  ORDER_TYPE_UPGRADE_RENEW,
  GRANT_PASS,
} from '@utils/constants'
import { isForeignCurrency, isOversea } from '@utils/assist'
import {
  STORE_INIT_PRODUCT,
  STORE_INIT_SERVICE,
  STORE_SET_ORDER_TYPE,
  STORE_LOAD_READY,
  STORE_SET_ORDER_PRICE,
  STORE_RESET_ORDER_PRICE,
  STORE_ORDER_PRODUCT_ID,
  STORE_CID_LIST,
  STORE_UNSUBMIT_STATUS
} from '../mutationTypes'

const state = {
  isReady: false,
  products: {
    new: [],
    append: [],
    upgrade: [],
    renew: [],
    upgrade_renew: [],
    currency_id: 1,
    no_pay_order_number: 0,
  },
  services: [],
  orderPrice: {
    vouchers: {}
  },
  orderType: 0,
  orderProductId: "",
  cid_list: [],
  isUnSubmit: false
}

const getters = {
  // 是否可以新签
  canNew (store) {
    return store.products.new.length > 0
  },
  // 是否可以追加
  canAppend (store) {
    return store.products.append.length > 0
  },
  // 是否可以升级
  canUpgrade (store, getters, rootState, rootGetter) {
    // 阿里白名单是否可以升级
    const isCanBuy = rootGetter['main/isCanBuy']
    return store.products.upgrade.length > 0 && isCanBuy
  },
  // 是否可以续约
  canRenew (store, getters, rootState, rootGetter) {
    // 阿里白名单是否可以续约
    const isCanBuy = rootGetter['main/isCanBuy']
    return store.products.renew.length > 0 && isCanBuy
  },
  // 是否可以升级+续约
  canUpgradeRenew (store) {
    return store.products.upgrade_renew.length > 0
  },
  // 选中订单类型下的可选产品
  productOfOrderType (store) {
    const typeMap = {
      [ORDER_TYPE_NEW]: 'new',
      [ORDER_TYPE_APPEND]: 'append',
      [ORDER_TYPE_RENEW]: 'renew',
      [ORDER_TYPE_UPGRADE]: 'upgrade',
      [ORDER_TYPE_UPGRADE_RENEW]: 'upgrade_renew'
    }
    return store.products[typeMap[store.orderType]] || []
  },
  // 是否买断
  isBuyOut (store) {
    return store.products.is_buyout > 0
  },
  // 是否首次购买
  isFirstPurchase(state) {
    return state.products.pay_order_num < 1
  },
  // 可用优惠券列表
  vouchers (store) {
    return store.orderPrice.vouchers
  },
  // 优惠券是否为空
  isNotEmptyOfVoucher (store) {
    const { agency_vouchers, client_vouchers } = store.orderPrice.vouchers
    const isNotEmpty = data => data && data.length > 0
    return isNotEmpty(agency_vouchers) || isNotEmpty(client_vouchers)
  },
  // 所选产品订单价格
  finalPrice (store) {
    const { amount, discount, original_amount: originalAmount, discount_amount: discountAmount } = store.orderPrice
    return { amount, discount, originalAmount, discountAmount }
  },
  // 计价的产品及服务
  orderProduct(store) {
    return store.orderPrice.product_list
  },
  // 是否外币购买（非人民币）
  isForeignCurrency(state) {
    return isForeignCurrency(state.products.currency_id)
  },
  // 是否是海外客户
  isOversea(state) {
    return isOversea(state.products.is_oversea)
  },
  // 货币符号
  currency (store) {
    return store.products.currency_label
  },
  // 公司名称——提交发票时的发票抬头
  fullName (store) {
    return store.products.full_name
  },
  // 是否需要升级和续约提示
  isReducedVersionHints (store) {
    return store.orderPrice.reduced_version_hints
  }
}

const actions = {
  //获取产品服务列表
  async fetchProductService({ commit }, contract_id) {
    const services = await StoreApi.fetchProductService({
      order_product_id: contract_id
    });
    commit(STORE_INIT_SERVICE, services);
  },
  // 获取产品数据
  async fetchProduct({ commit }) {
    // 订单类型
    const fetchTypeList = StoreApi.fetchProductType()
    // 产品服务
    const fetchProductService = StoreApi.fetchProductService()
    
    const typeList = await fetchTypeList
    const services = await fetchProductService
    // 将用户的 typeLis 产品数据放在 state.products 中
    commit(STORE_INIT_PRODUCT, typeList)
    commit(STORE_INIT_SERVICE, services)
    commit(STORE_LOAD_READY, true)
  },
  // 订单价格
  async fetchOrderPrice({ commit }, params) {
    if (params.params.length < 1) {
      return commit(STORE_RESET_ORDER_PRICE)
    }
    const data = await StoreApi.fetchOrderPrice({
      ...params,
      client_id: window.__xm_wx.clientId
    })
    commit(STORE_SET_ORDER_PRICE, data)
  },
  async saveOrder(state, params) {
    return StoreApi.saveOrder(params)
  },
  async fetchGoogleCid({ commit }) {
    const cid_list = await StoreApi.fetchGoogleCidList({ grant_status: GRANT_PASS });
    commit(STORE_CID_LIST, cid_list);
  }
}

const mutations = {
  [STORE_INIT_PRODUCT](state, data) {
    state.products = Object.freeze(data)
  },
  [STORE_INIT_SERVICE](state, data) {
    const result = data.reduce((result, item) => {
      result[item.product_id] = item
      return result
    }, {})
    state.services = Object.freeze(result)
  },
  [STORE_SET_ORDER_PRICE](state, data) {
    state.orderPrice = Object.freeze(data)
  },
  [STORE_RESET_ORDER_PRICE] (state) {
    state.orderPrice = {
      amount: 0,
      discount: 0,
      originalAmount: 0,
      vouchers: {}
    }
  },
  [STORE_LOAD_READY](state, data) {
    state.isReady = data
  },
  [STORE_SET_ORDER_TYPE](state, data) {
    state.orderType = data
  },
  [STORE_ORDER_PRODUCT_ID](state, data) {
    state.orderProductId = data;
  },
  [STORE_CID_LIST](state, data){
    state.cid_list = data;
  },
  [STORE_UNSUBMIT_STATUS](state, data){
    state.isUnSubmit = data
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}